// src/contexts/GoalDashboardContext.tsx
import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { goalSettingService } from "../service/goal-setting.service";
import { GoalItem } from "../models/GoalSettingDashboard";
import { AvailableSubOrganization } from "../models/GoalSetting";
import { useNavigate } from "react-router-dom";
import alertService from "../components/alert/alertService";
import { Trans, useTranslation } from "react-i18next";
import { common } from "../i18n";

interface GoalDashboardState {
  allGoals: GoalItem[];
  selectedGoalIds: string[];
  setSelectedGoalIds: (ids: string[]) => void;
  displayedGoals: GoalItem[];
  setDisplayedGoals: (goals: GoalItem[]) => void;
}

const GoalDashboardContext = createContext<GoalDashboardState | null>(null);

export const GoalDashboardProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [allGoals, setAllGoals] = useState<GoalItem[]>([]);
  const [selectedGoalIds, setSelectedGoalIds] = useState<string[]>([]);
  const [displayedGoals, setDisplayedGoals] = useState<GoalItem[]>([]);
  const [isReady, setIsReady] = useState(false);

  const { t } = useTranslation([common], { nsMode: "fallback" });

  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      let result;
      try {
        result = await goalSettingService.getAvailableSuborganizationList();
      } catch {
        // error alert already shown by service
        return;
      }
      if (!result) return;

      const hasEmission = result.suborganizations.some((s) => s.hasEmission);

      let res;
      try {
        res = await goalSettingService.getDashboard();
      } catch {
        // error alert already shown by service
        return;
      }
      setAllGoals(res?.goals || []);

      if (!hasEmission) {
        await new Promise<void>((resolve) =>
          requestAnimationFrame(() => resolve()),
        );

        await alertService.confirm(
          t("Historical-Data-Not-Found"),
          <Trans
            t={t}
            i18nKey="Historical-Data-At-Least-Description"
            components={{ span: <span style={{ color: "var(--red)" }} /> }}
          />,
          t("Add-Emission"),
          "var(--dark)",
          true,
          undefined,
          undefined,
          undefined,
        );
        navigate("/emission/add");
        return;
      }

      // ✅ ลบออก ไม่ต้องใช้ isReady แล้ว
    };

    init();
  }, []);

  useEffect(() => {
    const selected = allGoals.filter((g) =>
      selectedGoalIds.includes(g.goalRefId),
    );
    setDisplayedGoals(selected);
  }, [selectedGoalIds, allGoals]);

  // ✅ ยังไม่ ready → ไม่ render children (กันกระพริบหรือ race condition)
  // if (!isReady) return null;

  return (
    <GoalDashboardContext.Provider
      value={{
        allGoals,
        selectedGoalIds,
        setSelectedGoalIds,
        displayedGoals,
        setDisplayedGoals,
      }}
    >
      {children}
    </GoalDashboardContext.Provider>
  );
};

export const useGoalDashboardContext = () => {
  const ctx = useContext(GoalDashboardContext);
  if (!ctx)
    throw new Error(
      "useGoalDashboardContext must be used within GoalDashboardProvider",
    );
  return ctx;
};
